// #region: Class definition *********************************************************************/
/**
 * Abstract base class for web components that provides shared functionality.
 * Contains common logic for localization, lifecycle management, and CSS utilities.
 * @class WebComponent
 * @extends HTMLElement
 */
class WebComponent extends HTMLElement {

    // #region: Private Fields *******************************************************************/
    /**
     * Current locale for this component.
     * @type {string|null}
     * @private
     *
     * @memberof WebComponent
     */
    #locale;

    /**
     * Event handler for global locale changes.
     * @type {Function|null}
     * @private
     *
     * @memberof WebComponent
     */
    #localeChangeHandler;

    /**
     * Registered locale namespaces for this component.
     * @type {string[]}
     * @private
     *
     * @memberof WebComponent
     */
    #localeNamespaces;
    // #endregion ********************************************************************************/

    // #region: Constructor **********************************************************************/
    /**
     * Initializes a new WebComponent instance.
     * Sets up internal state, iOS detection, and localization fields.
     * @constructor
     *
     * @memberof WebComponent
     */
    constructor() {
        super();

        // Contains the computed styles for the component, actual values are filled when the component is added to the DOM (connectedCallback).
        this.computedStyles = null;

        // Make state(s) selectable in CSS using the pseudo element ':state(name)'.
        this.internals = this.attachInternals();

        // Android detection for platform-specific functionality.
        this.isAndroid = /Android/.test(window.navigator.userAgent);

        // Sadly, iOS has several limitations that we need to address.
        this.isIos = (/iPad|iPhone|iPod/).test(window.navigator.platform) || (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);

        // Use to calculate css values set in 'rem', actual values are filled when the component is added to the DOM (connectedCallback).
        this.rootFontSize = null;

        // Initialize localization fields.
        this.#locale = null;
        this.#localeNamespaces = [];
        this.#localeChangeHandler = null;
    }
    // #endregion ********************************************************************************/

    // #region: Build ****************************************************************************/
    /**
     * Registered tag name for this web component.
     * This is used by the browser to identify and register the custom element.
     * @type {string}
     * @static
     *
     * @memberof WebComponent
     */
    static tagName = 'web-component';

    /**
     * Returns the string representation of this class instance.
     * @returns {string} The string representation of this class.
     *
     * @memberof WebComponent
     */
    toString() {
        return '[WebComponent]';
    }

    /**
     * Returns the string representation of this class.
     * @returns {string} The string representation of this class.
     * @static
     *
     * @memberof WebComponent
     */
    static toString() {
        return '[WebComponent]';
    }

    /**
     * Returns the version number of this class.
     * @returns {string} The version number of this class.
     * @static
     *
     * @memberof WebComponent
     */
    static version() {
        return '1.1.0';
    }
    // #endregion ********************************************************************************/

    // #region: Static Properties ****************************************************************/
    /**
     * Defines which attributes should trigger the attributeChangedCallback when modified.
     * Override in derived classes and include these base attributes if needed.
     * This is a Web Components standard method that tells the browser which attributes to monitor.
     * @returns {string[]} Array of attribute names to observe.
     * @static
     *
     * @memberof WebComponent
     */
    static get observedAttributes() {
        return ['disabled', 'locale', 'read-only'];
    }
    // #endregion ********************************************************************************/

    // #region: Properties ***********************************************************************/
    /**
     * Gets whether this component is disabled.
     * @returns {boolean} True if disabled, false otherwise.
     *
     * @memberof WebComponent
     */
    get disabled() {
        return this.getAttribute('disabled') !== null;
    }

    /**
     * Sets whether this component is disabled.
     * @param {boolean|null} value True to disable, false/null to enable.
     *
     * @memberof WebComponent
     */
    set disabled(value) {
        if (value === null || value === false) {
            this.removeAttribute('disabled');
        }
        else {
            this.setAttribute('disabled', '');
        }
    }

    /**
     * Gets the current locale for this component.
     * @returns {string} The locale identifier.
     *
     * @memberof WebComponent
     */
    get locale() {
        return this.#locale || this.getAttribute('locale') || (window.localeManager?.getCurrentLocale()) || navigator.language || 'nl-NL';
    }

    /**
     * Sets the locale for this component.
     * @param {string} value The locale identifier.
     *
     * @memberof WebComponent
     */
    set locale(value) {
        const oldLocale = this.#locale;

        this.#locale = value;

        if (oldLocale !== value) {
            this.onLocaleChanged(oldLocale, value);
        }
    }

    /**
     * Gets the registered locale namespaces for this component.
     * @returns {string[]} Array of namespace names.
     *
     * @memberof WebComponent
     */
    get localeNamespaces() {
        return this.#localeNamespaces;
    }

    /**
     * Gets whether this component is read-only.
     * @returns {boolean} True if read-only, false otherwise.
     *
     * @memberof WebComponent
     */
    get readOnly() {
        return this.getAttribute('read-only') !== null;
    }

    /**
     * Sets whether this component is read-only.
     * @param {boolean|null} value True to make read-only, false/null to make editable.
     *
     * @memberof WebComponent
     */
    set readOnly(value) {
        if (value === null || value === false) {
            this.removeAttribute('read-only');
        }
        else {
            this.setAttribute('read-only', '');
        }
    }
    // #endregion ********************************************************************************/

    // #region: Lifecycle events *****************************************************************/
    /**
     * Fires when the element is added to a new document.
     * The connectedCallback will be called after this.
     *
     * @memberof WebComponent
     */
    adoptedCallback() {
    }

    /**
     * Processes changes to any of the observed attributes.
     * Also called on initialization.
     * @param {string} name The name of the attribute that changed.
     * @param {string} oldValue The previous value of the attribute.
     * @param {string} newValue The new value of the attribute.
     *
     * @memberof WebComponent
     */
    attributeChangedCallback(name, oldValue, newValue) {
        switch (name) {

            case 'disabled':
                // Update internal state based on attribute presence.
                if (newValue !== null) {
                    this.internals.states.add('disabled');
                }
                else {
                    this.internals.states.delete('disabled');
                }

                break;

            case 'locale':
                if (oldValue !== newValue) {
                    this.#locale = newValue;
                }

                break;

            case 'read-only':
                // Update internal state based on attribute presence.
                if (newValue !== null) {
                    this.internals.states.add('read-only');
                }
                else {
                    this.internals.states.delete('read-only');
                }

                break;

            default:
                break;
        
        }
    }

    /**
     * Called when the component is added to the DOM.
     * Sets up computed styles, root font size, locale data preloading, and locale change listeners.
     *
     * @memberof WebComponent
     */
    connectedCallback() {
        this.rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
        this.computedStyles = getComputedStyle(this);

        // Set up global locale change listener if LocaleManager is available.
        if (window.localeManager && !this.#localeChangeHandler) {
            this.#localeChangeHandler = (event) => {
                if (!this.hasAttribute('locale')) {
                    // Only update if component doesn't have explicit locale attribute.
                    this.onLocaleChanged(this.#locale, event.detail.locale);
                }
            };

            window.addEventListener('localechange', this.#localeChangeHandler);
        }

        if (document.readyState !== 'loading') {
            this.initialize();

            return;
        }

        document.addEventListener('DOMContentLoaded', () => {
            return this.initialize();
        });
    }

    /**
     * Called when the component is removed from the DOM.
     * Performs cleanup of event listeners and resources.
     *
     * @memberof WebComponent
     */
    disconnectedCallback() {
        // Remove global locale change listener.
        if (this.#localeChangeHandler) {
            window.removeEventListener('localechange', this.#localeChangeHandler);
            this.#localeChangeHandler = null;
        }
    }
    // #endregion ********************************************************************************/

    // #region: Public Methods *******************************************************************/
    /**
     * Gets locale data for a specific namespace.
     * Directly accesses localeManager for immediate availability.
     * @param {string} namespace The namespace to get data for.
     * @returns {Object|null} The locale data or null if not found.
     *
     * @memberof WebComponent
     */
    getLocaleData(namespace) {
        // Check if this namespace is registered for this component.
        if (!this.#localeNamespaces.includes(namespace)) {
            window.isDebugMode && console.warn(`Namespace '${namespace}' not registered for component ${this.constructor.name}`);

            return null;
        }

        if (!window.localeManager) {
            window.isDebugMode && console.warn('LocaleManager not available');

            return null;
        }

        const currentLocale = this.#locale || window.localeManager.getCurrentLocale();

        return window.localeManager.get(currentLocale, namespace);
    }

    /**
     * Gets a localized string value with optional placeholder replacement.
     * Uses the cached locale data for better performance.
     * @param {string} namespace The namespace.
     * @param {string} path The dot-notation path to the value.
     * @param {string} [defaultValue=''] The default value if not found.
     * @param {Object} [values] Values for placeholder replacement.
     * @returns {string} The localized string.
     *
     * @memberof WebComponent
     */
    getLocalizedString(namespace, path, defaultValue = '', values = null) {
        const data = this.getLocaleData(namespace);

        if (!data || !window.localeManager) {
            return defaultValue;
        }

        const value = window.localeManager.getValue(data, path, defaultValue);

        if (values && typeof value === 'string') {
            return window.localeManager.format(value, values);
        }

        return value;
    }

    /**
     * Initializes this web component.
     * Runs on document load (see: connectedCallback).
     * @returns {void}
     *
     * @memberof WebComponent
     */
    initialize() {}

    /**
     * Called when the locale changes.
     * Clears locale cache and can be overridden in derived classes to update localized content.
     * @param {string} oldLocale The previous locale.
     * @param {string} newLocale The new locale.
     *
     * @memberof WebComponent
     */
    onLocaleChanged(oldLocale, newLocale) {
        // Update the locale and let components re-query localeManager directly.
        this.#locale = newLocale;
        
        // Override in derived classes for additional locale change handling.
    }

    /**
     * Parses a CSS variable value and converts it to pixels.
     * @param {string} property The CSS property to get the value for.
     * @param {string|number} defaultValue The default (rem) value to use if parsing fails.
     * @returns {number} The calculated or default value as a float.
     *
     * @memberof WebComponent
     */
    parseCssValue(property, defaultValue) {
        const size = this.computedStyles.getPropertyValue(property).trim();

        return this.parseRemValue(size, defaultValue);
    }

    /**
     * Converts rem-based CSS values to pixel values.
     * @param {string} size The size string with a rem value (e.g., '15rem').
     * @param {string|number} defaultValue The default (rem) value to use if parsing fails.
     * @returns {number} The calculated or default value as a float.
     *
     * @memberof WebComponent
     */
    parseRemValue(size, defaultValue) {
        let value = parseFloat(size);

        if (isNaN(value)) {
            value = defaultValue;
        }

        return size.endsWith('rem') ? value * this.rootFontSize : value;
    }

    /**
     * Registers locale namespace (alement tag name) that this component uses and returns the locale resources.
     * Should be called in the constructor of derived classes immediately after super().
     * @returns {Object} Object containing cached locale resources for each registered namespace.
     *
     * @memberof WebComponent
     */
    registerLocaleNamespace() {
        const namespace = this.tagName.toLowerCase();

        this.#localeNamespaces.push(namespace);

        // Initialize the current locale once to avoid repeated calls to getCurrentLocale().
        if (!this.#locale) {
            const currentLocale = window.localeManager?.getCurrentLocale();

            // Ensure the locale is lowercase for consistency.
            this.#locale = (currentLocale || navigator.language || 'nl-NL').toLowerCase();
        }

        return window.localeManager ? window.localeManager.get(this.#locale, namespace) : {};
    }

    /**
     * Generic method to handle content changes and re-check validation.
     * Only re-validating when the component is currently in an error state.
     * @param {boolean} [force=false] - Force validation even if not currently in error state.
     * @returns {boolean} True if validation passed or wasn't needed, false if validation failed.
     *
     * @memberof WebComponent
     */
    updateValidationState(force = false) {
        // Only validate if we have a validator and it's currently showing an error (or forced).
        if (this.validator && (force || this.validator.hasBubble())) {
            try {
                return this.validator.validate({ required: true });
            }
            catch (error) {
                // If validation fails, log the error but don't throw.
                window.isDebugMode && console.error('Validation error in component:', error, this);

                return false;
            }
        }
        
        // If no validator or no current error, assume valid.
        return true;
    }

    /**
     * Abstract validation method that must be implemented by derived classes.
     * @returns {boolean} True if valid, false if not.
     * @throws {Error} When not implemented in derived class.
     *
     * @memberof WebComponent
     */
    validate() {
        throw new Error('Validate Not Implemented!');
    }
    // #endregion ********************************************************************************/

}
// #endregion ************************************************************************************/
